import { useEffect, useState } from "react";
import { api, fmtINR, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";

export default function Budgets() {
  const { user } = useAuth();
  const [centres, setCentres] = useState([]);
  const [budgets, setBudgets] = useState([]);
  const [summary, setSummary] = useState([]);
  const [f, setF] = useState({ centre_id: "", year: new Date().getFullYear(), month: "", category: "", amount: 0 });
  const canEdit = ["admin", "ho_approver"].includes(user?.role);

  const load = () => Promise.all([
    api.get("/centres").then((r) => setCentres(r.data)),
    api.get("/budgets").then((r) => setBudgets(r.data)),
    api.get("/dashboard/centre-summary").then((r) => setSummary(r.data)),
  ]);
  useEffect(() => { load(); }, []);

  const save = async () => {
    if (!f.centre_id) return toast.error("Select centre");
    try {
      await api.post("/budgets", { ...f, month: f.month || null, category: f.category || null, amount: Number(f.amount) });
      toast.success("Budget saved");
      setF({ centre_id: "", year: new Date().getFullYear(), month: "", category: "", amount: 0 });
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="p-8 space-y-6" data-testid="budgets-root">
      <div>
        <div className="text-[10px] tracking-[0.3em] uppercase text-zinc-500">Planning</div>
        <h1 className="font-heading text-4xl mt-1 tracking-tight">Budgets</h1>
      </div>

      {canEdit && (
        <div className="bg-white border border-zinc-200 p-6 space-y-4">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Set Budget</div>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            <div><Label className="text-xs uppercase tracking-widest">Centre</Label>
              <Select value={f.centre_id} onValueChange={(v) => setF({ ...f, centre_id: v })}>
                <SelectTrigger className="rounded-sm h-10 mt-2"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{centres.map((c) => <SelectItem key={c.id} value={c.id}>{c.code} · {c.name}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label className="text-xs uppercase tracking-widest">Year</Label><Input type="number" value={f.year} onChange={(e) => setF({ ...f, year: Number(e.target.value) })} className="rounded-sm h-10 mt-2 tabular-nums" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Month (opt.)</Label><Input type="number" min="1" max="12" value={f.month} onChange={(e) => setF({ ...f, month: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Category (opt.)</Label><Input value={f.category} onChange={(e) => setF({ ...f, category: e.target.value })} className="rounded-sm h-10 mt-2" /></div>
            <div><Label className="text-xs uppercase tracking-widest">Amount</Label><Input type="number" value={f.amount} onChange={(e) => setF({ ...f, amount: e.target.value })} className="rounded-sm h-10 mt-2 tabular-nums text-right" /></div>
          </div>
          <Button onClick={save} data-testid="save-budget" className="rounded-sm bg-zinc-900 hover:bg-zinc-800 text-white">Save Budget</Button>
        </div>
      )}

      <div className="bg-white border border-zinc-200">
        <div className="p-6 border-b border-zinc-200">
          <div className="text-[10px] uppercase tracking-[0.2em] text-zinc-500">Centre-wise Utilisation</div>
          <div className="font-heading text-lg mt-1">Budget vs Actual</div>
        </div>
        <table className="w-full text-sm">
          <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
            <tr>
              <th className="text-left p-4">Centre</th>
              <th className="text-right p-4">Annual Budget</th>
              <th className="text-right p-4">Actual Expense</th>
              <th className="text-right p-4">Balance</th>
              <th className="text-left p-4">Utilisation</th>
            </tr>
          </thead>
          <tbody className="tabular-nums">
            {summary.map((r) => (
              <tr key={r.centre_id} className="border-t border-zinc-100">
                <td className="p-4"><div className="font-medium">{r.name}</div><div className="text-xs text-zinc-500">{r.code}</div></td>
                <td className="p-4 text-right">{fmtINR(r.annual_budget)}</td>
                <td className="p-4 text-right">{fmtINR(r.expense)}</td>
                <td className={`p-4 text-right ${r.annual_budget - r.expense < 0 ? "text-rose-700" : "text-emerald-700"}`}>{fmtINR(r.annual_budget - r.expense)}</td>
                <td className="p-4">
                  <div className="flex items-center gap-3">
                    <div className="flex-1 h-2 bg-zinc-100">
                      <div className={`h-full ${r.utilisation > 100 ? "bg-rose-600" : r.utilisation > 90 ? "bg-rose-500" : r.utilisation > 75 ? "bg-amber-500" : "bg-emerald-600"}`}
                           style={{ width: `${Math.min(100, r.utilisation)}%` }} />
                    </div>
                    <span className="text-xs w-12 text-right">{r.utilisation}%</span>
                    {r.utilisation > 90 && <span className="text-[10px] uppercase tracking-widest px-1.5 py-0.5 bg-rose-50 text-rose-700 border border-rose-300">Alert</span>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {budgets.length > 0 && (
        <div className="bg-white border border-zinc-200">
          <div className="p-6 border-b border-zinc-200 text-[10px] uppercase tracking-[0.2em] text-zinc-500">Configured Budgets</div>
          <table className="w-full text-sm">
            <thead className="bg-zinc-50 text-[10px] uppercase tracking-widest text-zinc-500">
              <tr><th className="text-left p-4">Centre</th><th className="text-left p-4">Year</th><th className="text-left p-4">Month</th><th className="text-left p-4">Category</th><th className="text-right p-4">Amount</th></tr>
            </thead>
            <tbody className="tabular-nums">
              {budgets.map((b, i) => {
                const c = centres.find((x) => x.id === b.centre_id);
                return (
                  <tr key={b.id || `${b.centre_id}-${b.year}-${b.month || "all"}-${b.category || "all"}-${i}`} className="border-t border-zinc-100">
                    <td className="p-4">{c?.code || b.centre_id}</td>
                    <td className="p-4">{b.year}</td>
                    <td className="p-4">{b.month || "—"}</td>
                    <td className="p-4">{b.category || "All"}</td>
                    <td className="p-4 text-right">{fmtINR(b.amount)}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
